/*
 * <announcement-marquee> — decides whether the bar's one line has to move.
 *
 * The bar states a single continuous message and is meant to sit still; it may
 * scroll only when that message is wider than the screen. That is a question
 * about rendered text, so only the browser can answer it — Liquid cannot know
 * the viewport, the font that actually loaded, or what the countdown will read
 * once it upgrades.
 *
 * Nothing here is required for the bar to be legible: the server renders the
 * static, clipped state, and this element only ever adds the scrolling class on
 * top of it. A cache miss on this asset, or JS off, leaves a still bar.
 *
 * Wrapped in an IIFE for the same reason `launch-countdown.js` is: a page
 * carrying two hosts would emit the tag twice and the second run would throw on
 * the duplicate class declaration before reaching the guard below.
 */

(() => {
  const SCROLL_CLASS = 'announcement-bar__marquee--scroll';

  /*
   * Sub-pixel slack. `scroll_mode: off` and a line that fits are the same state
   * to a reader, and rounding a flex item's width against its container's must
   * not be what starts a marquee Douglas asked not to run.
   */
  const OVERFLOW_TOLERANCE = 1;

  class AnnouncementMarquee extends HTMLElement {
    connectedCallback() {
      if (this.dataset.scrollMode !== 'auto') return;

      this.marquee = this.querySelector('.announcement-bar__marquee');
      this.group = this.querySelector('.announcement-bar__marquee-group');
      if (!this.marquee || !this.group) return;

      /*
       * Both boxes, because either side of the comparison moves on its own. The
       * group is the line, and it is re-laid-out by everything that lands after
       * this element connects — the webfont swapping the fallback's metrics, the
       * countdown replacing its server sentence with a clock, an hour digit
       * dropping, the countdown retiring. The host is the room that line has, so
       * it answers viewport resizes. The observer's first delivery on `observe`
       * is the initial measurement.
       */
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this);
      this.observer.observe(this.group);
    }

    disconnectedCallback() {
      /* The guards above return before the observer exists in every mode but
       * `auto`. */
      if (this.observer) this.observer.disconnect();
    }

    /*
     * The group never shrinks (`flex-shrink: 0`) and is at least as wide as this
     * host (`min-width: 100%`), so its own box is the width the message needs —
     * no scroll container to interrogate, and the answer holds while the
     * animation translates it.
     */
    measure() {
      const line = this.group.getBoundingClientRect().width;
      const available = this.getBoundingClientRect().width;

      this.marquee.classList.toggle(SCROLL_CLASS, line - available > OVERFLOW_TOLERANCE);
    }
  }

  if (!customElements.get('announcement-marquee')) {
    customElements.define('announcement-marquee', AnnouncementMarquee);
  }
})();
